import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CurrencyInr, DownloadSimple, Wallet, Gear, CaretDown, CaretRight, Warning, Handshake, Plus } from "@phosphor-icons/react";
import { dmY } from "@/lib/dateFmt";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function isoNDaysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
function inr(n) { return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function timeHM(iso) {
    if (!iso) return "—";
    return iso.slice(11, 16) || "—";
}

export default function CashRegister() {
    const { user } = useAuth();
    const [fromDate, setFromDate] = useState(isoNDaysAgo(30));
    const [toDate, setToDate] = useState(todayISO());
    const [reg, setReg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [opening, setOpening] = useState(null);
    const [obOpen, setObOpen] = useState(false);
    const [obBal, setObBal] = useState("0");
    const [obDate, setObDate] = useState(todayISO());
    const [expanded, setExpanded] = useState(() => new Set());
    // v1.28.64 — "Record Cash from Jitu" modal
    const [depOpen, setDepOpen] = useState(false);
    const [dep, setDep] = useState({ date: todayISO(), amount: "", source: "Jitu", description: "" });

    const canEditOpening = user?.role === "admin";
    const toggleExpand = (d) => {
        setExpanded((prev) => {
            const s = new Set(prev);
            if (s.has(d)) s.delete(d); else s.add(d);
            return s;
        });
    };

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const [r1, r2] = await Promise.all([
                api.get(`/cash/register?from_date=${fromDate}&to_date=${toDate}`),
                api.get("/cash/opening-balance"),
            ]);
            setReg(r1.data);
            setOpening(r2.data);
            setObBal(String(r2.data?.opening_balance ?? 0));
            setObDate(r2.data?.opening_date || todayISO());
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }, [fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onCash = () => load();
        window.addEventListener("micm-cash-updated", onCash);
        return () => window.removeEventListener("micm-cash-updated", onCash);
    }, [load]);

    const saveOpening = async () => {
        const bal = parseFloat(obBal);
        if (isNaN(bal) || bal < 0) { toast.error("Enter a valid opening balance"); return; }
        try {
            await api.put("/cash/opening-balance", { opening_balance: bal, opening_date: obDate });
            toast.success("Opening balance saved");
            setObOpen(false);
            load();
            window.dispatchEvent(new CustomEvent("micm-cash-updated"));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const saveDeposit = async () => {
        const amt = parseFloat(dep.amount);
        if (!amt || amt <= 0) { toast.error("Enter amount > 0"); return; }
        try {
            await api.post("/cash/deposits", {
                date: dep.date, amount: amt,
                source: (dep.source || "Jitu").trim() || "Jitu",
                description: (dep.description || "").trim(),
                payment_mode: "Cash",
            });
            toast.success(`₹${inr(amt)} cash received from ${dep.source || "Jitu"}`);
            setDepOpen(false);
            setDep({ date: todayISO(), amount: "", source: "Jitu", description: "" });
            load();
            window.dispatchEvent(new CustomEvent("micm-cash-updated"));
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const downloadPdf = () => {
        api.get(`/cash/register.pdf?from_date=${fromDate}&to_date=${toDate}`, { responseType: "blob" })
            .then((r) => {
                const dd = fromDate.split("-").reverse().join("-");
                const blob = new Blob([r.data], { type: "application/pdf" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `${dd}_cash_register_${fromDate}_${toDate}.pdf`;
                a.click();
                URL.revokeObjectURL(a.href);
            })
            .catch((e) => toast.error(formatApiError(e)));
    };

    const days = reg?.days || [];
    const hasNegative = days.some((d) => d.closing < 0) || (reg && reg.opening_balance < 0);

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900">
                        <Wallet size={22} weight="fill" className="text-emerald-700" />
                        Cash Register
                    </h1>
                    <p className="text-sm text-slate-500">Physical cash only · +IN when Jitu hands over cash, −OUT on cash paid to parties.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => setDepOpen(true)}
                            data-testid="cash-deposit-btn"
                            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-700">
                        <Handshake size={14} weight="fill" /> Cash Received from Jitu
                    </button>
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                        From
                        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                               data-testid="cash-from-date"
                               className="rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-emerald-400" />
                    </label>
                    <label className="flex items-center gap-1 text-xs text-slate-600">
                        To
                        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                               data-testid="cash-to-date"
                               className="rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-emerald-400" />
                    </label>
                    <button type="button" onClick={downloadPdf} disabled={!reg}
                            data-testid="cash-download-pdf"
                            className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                        <DownloadSimple size={14} weight="bold" /> Download PDF
                    </button>
                    {canEditOpening && (
                        <button type="button" onClick={() => setObOpen(true)}
                                data-testid="cash-set-opening-btn"
                                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                            <Gear size={14} weight="bold" /> Opening Balance
                        </button>
                    )}
                </div>
            </div>

            {hasNegative && (
                <div className="mb-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900"
                     data-testid="cash-negative-warning">
                    <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-rose-600" />
                    <div>
                        <div className="font-semibold">Cash balance is negative in this range.</div>
                        <div className="text-xs text-rose-800/90">
                            Cash-mode payouts exceed the Jitu cash handovers recorded. Record every physical cash handover via <b>Cash Received from Jitu</b>{canEditOpening && (<>, or <button type="button" onClick={() => setObOpen(true)} className="underline underline-offset-2 font-semibold">set the tenant opening cash balance</button></>)} to correct it.
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                {[
                    { label: "Opening", value: reg?.opening_balance, tint: "bg-slate-50 text-slate-800" },
                    { label: "Cash In", value: reg?.total_in, tint: "bg-emerald-50 text-emerald-800" },
                    { label: "Cash Out", value: reg?.total_out, tint: "bg-rose-50 text-rose-800" },
                    { label: "Expenses", value: reg?.total_expenses, tint: "bg-amber-50 text-amber-800" },
                    { label: "Closing", value: reg?.closing_balance, tint: "bg-emerald-100 text-emerald-900 font-bold" },
                ].map((c) => (
                    <div key={c.label} data-testid={`cash-kpi-${c.label.toLowerCase().replace(/\s+/g, '-')}`}
                         className={`rounded-xl border border-slate-200 p-3 ${c.tint}`}>
                        <div className="text-[11px] font-bold uppercase tracking-wider">{c.label}</div>
                        <div className="mt-1 flex items-center gap-0.5 text-lg tabular-nums">
                            <CurrencyInr size={14} weight="bold" />
                            {inr(c.value)}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-sm" data-testid="cash-register-table">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                        <tr>
                            <th className="w-6 px-2 py-2"></th>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-right">Opening</th>
                            <th className="px-3 py-2 text-right">Cash In</th>
                            <th className="px-3 py-2 text-right">Cash Out</th>
                            <th className="px-3 py-2 text-right">Expenses</th>
                            <th className="px-3 py-2 text-right">Closing</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {busy && (<tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>)}
                        {!busy && days.length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No cash movement in this range.</td></tr>
                        )}
                        {!busy && days.map((d) => {
                            const open = expanded.has(d.date);
                            const isNeg = d.closing < 0;
                            const rows = d.rows || [];
                            let running = d.opening;
                            return (
                                <React.Fragment key={d.date}>
                                    <tr
                                        onClick={() => toggleExpand(d.date)}
                                        data-testid={`cash-day-row-${d.date}`}
                                        className="cursor-pointer select-none hover:bg-emerald-50/40"
                                    >
                                        <td className="px-2 py-2 text-slate-500">
                                            {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                                        </td>
                                        <td className="px-3 py-2 font-medium text-slate-800">
                                            {dmY(d.date)}
                                            {isNeg && (
                                                <span className="ml-2 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-800">
                                                    Negative
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">₹{inr(d.opening)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700">+₹{inr(d.in)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-rose-700">−₹{inr(d.out)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-amber-700">−₹{inr(d.expense)}</td>
                                        <td className={`px-3 py-2 text-right tabular-nums font-bold ${isNeg ? "text-rose-700" : "text-slate-900"}`}>
                                            ₹{inr(d.closing)}
                                        </td>
                                    </tr>
                                    {open && (
                                        <tr data-testid={`cash-day-detail-${d.date}`}>
                                            <td colSpan={7} className="bg-slate-50 px-4 py-3">
                                                {rows.length === 0 ? (
                                                    <div className="text-xs text-slate-500">No individual entries.</div>
                                                ) : (
                                                    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
                                                        <table className="min-w-full text-xs">
                                                            <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-600">
                                                                <tr>
                                                                    <th className="px-3 py-1.5 text-left">Time</th>
                                                                    <th className="px-3 py-1.5 text-left">Source / Party</th>
                                                                    <th className="px-3 py-1.5 text-left">Ref</th>
                                                                    <th className="px-3 py-1.5 text-left">Type</th>
                                                                    <th className="px-3 py-1.5 text-right">Cash In</th>
                                                                    <th className="px-3 py-1.5 text-right">Cash Out</th>
                                                                    <th className="px-3 py-1.5 text-left">Remarks</th>
                                                                    <th className="px-3 py-1.5 text-right">Balance</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-100">
                                                                {rows.map((r) => {
                                                                    const cashIn = r.kind === "in" ? r.amount : 0;
                                                                    const cashOut = (r.kind === "out" || r.kind === "expense") ? r.amount : 0;
                                                                    running = running + cashIn - cashOut;
                                                                    const isDeposit = r.kind_label === "deposit";
                                                                    return (
                                                                        <tr key={r.id} className="hover:bg-slate-50">
                                                                            <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-slate-700">{timeHM(r.time)}</td>
                                                                            <td className="px-3 py-1.5">
                                                                                {isDeposit ? (
                                                                                    <span className="inline-flex items-center gap-1 font-medium text-emerald-800">
                                                                                        <Handshake size={12} weight="fill" /> {r.party_name || "Jitu"}
                                                                                    </span>
                                                                                ) : r.kind_label === "expense" ? (
                                                                                    <span className="text-amber-800">{r.description || "Expense"}</span>
                                                                                ) : (
                                                                                    <span className="text-slate-900">{r.party_name || <em className="text-slate-400">(no party)</em>}</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-3 py-1.5 text-slate-600">{r.utr || r.reference || "—"}</td>
                                                                            <td className="px-3 py-1.5">
                                                                                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${isDeposit ? "bg-emerald-100 text-emerald-800" : (r.kind_label === "out" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800")}`}>
                                                                                    {isDeposit ? "Deposit" : r.payment_mode || (r.kind_label === "expense" ? "Expense" : "Cash")}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{cashIn ? `+₹${inr(cashIn)}` : ""}</td>
                                                                            <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">{cashOut ? `−₹${inr(cashOut)}` : ""}</td>
                                                                            <td className="px-3 py-1.5 text-slate-500">{r.description || ""}</td>
                                                                            <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${running < 0 ? "text-rose-700" : "text-slate-900"}`}>₹{inr(running)}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {opening && (
                <div className="mt-3 text-xs text-slate-500">
                    Tenant opening baseline: ₹{inr(opening.opening_balance)} as of {dmY(opening.opening_date)}
                    {opening.updated_at ? ` · last set by ${opening.updated_by || "admin"}` : ""}
                </div>
            )}

            {depOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
                     data-testid="cash-deposit-modal" role="dialog" aria-modal="true">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
                            <Handshake size={16} weight="fill" className="text-emerald-700" />
                            <div className="text-sm font-bold uppercase tracking-wider text-slate-700">Cash Received from Jitu</div>
                        </div>
                        <div className="space-y-3 px-5 py-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Date</label>
                                <input type="date" value={dep.date}
                                       onChange={(e) => setDep({ ...dep, date: e.target.value })}
                                       data-testid="cash-deposit-date"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Amount (₹)</label>
                                <input type="number" min="0" step="0.01" value={dep.amount}
                                       onChange={(e) => setDep({ ...dep, amount: e.target.value })}
                                       placeholder="e.g. 50000"
                                       data-testid="cash-deposit-amount"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Source</label>
                                <input type="text" value={dep.source}
                                       onChange={(e) => setDep({ ...dep, source: e.target.value })}
                                       data-testid="cash-deposit-source"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-400" />
                                <div className="mt-1 text-[11px] text-slate-500">Default: Jitu. Change if the physical cash came from someone else.</div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Remarks (optional)</label>
                                <input type="text" value={dep.description}
                                       onChange={(e) => setDep({ ...dep, description: e.target.value })}
                                       data-testid="cash-deposit-desc"
                                       placeholder="e.g. Collection for July"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-400" />
                            </div>
                            <div className="rounded-md bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                                Only physical cash handovers move Cash In Hand. Digital transfers via Quick / Soulpay to Jitu are not tracked here.
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
                            <button type="button" onClick={() => setDepOpen(false)}
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                            <button type="button" onClick={saveDeposit}
                                    data-testid="cash-deposit-save"
                                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
                                <Plus size={12} weight="bold" /> Record Cash
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {obOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
                     data-testid="cash-opening-modal" role="dialog" aria-modal="true">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="border-b border-slate-100 px-5 py-3 text-sm font-bold uppercase tracking-wider text-slate-700">Set Opening Cash Balance</div>
                        <div className="space-y-3 px-5 py-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-600">Opening Balance (₹)</label>
                                <input type="number" min="0" step="0.01" value={obBal}
                                       onChange={(e) => setObBal(e.target.value)}
                                       data-testid="cash-opening-balance-input"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-400" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-600">From Date</label>
                                <input type="date" value={obDate}
                                       onChange={(e) => setObDate(e.target.value)}
                                       data-testid="cash-opening-date-input"
                                       className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-emerald-400" />
                            </div>
                            <div className="text-[11px] text-slate-500">
                                Cash In Hand will be calculated as opening balance plus every cash handover from this date onward.
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
                            <button type="button" onClick={() => setObOpen(false)}
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                            <button type="button" onClick={saveOpening}
                                    data-testid="cash-opening-save-btn"
                                    className="rounded-md bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
